import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon,
  ClockIcon, BanknotesIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ty-root * { box-sizing:border-box; }
  .ty-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tyFade .4s ease both; }
  @keyframes tyFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ty-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .ty-back:hover { color:#0d9488; }
  .ty-back svg { width:14px; height:14px; stroke-width:2.5; }
  .ty-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:1rem; }
  .ty-teacher { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .ty-avatar  { width:46px; height:46px; border-radius:13px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .ty-name    { font-size:1.3rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ty-empid   { font-family:'JetBrains Mono',monospace; font-size:.72rem; color:#9094a8; margin-top:2px; }
  .year-nav { display:flex; align-items:center; gap:8px; background:white; border:1px solid #e8ebf4; border-radius:12px; padding:6px 10px; flex-shrink:0; }
  .year-btn  { width:30px; height:30px; border-radius:8px; border:1px solid #e4e7f0; background:white; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .year-btn:hover { background:#f0fdfa; border-color:#0d9488; color:#0d9488; }
  .year-btn svg { width:14px; height:14px; stroke-width:2.5; }
  .year-label { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:600; color:#1a1d2e; min-width:52px; text-align:center; }
  .month-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; }
  .month-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem; cursor:pointer; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s,border-color .18s; animation:tyFade .4s ease both; }
  .month-card:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(13,148,136,.1); border-color:#99f6e4; }
  .month-card.future { opacity:.5; cursor:default; pointer-events:none; }
  .mc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:.9rem; gap:6px; flex-wrap:wrap; }
  .mc-name   { font-size:.88rem; font-weight:700; color:#0f1123; }
  .mc-badge  { display:inline-flex; align-items:center; gap:4px; font-size:.65rem; font-weight:700; padding:3px 8px; border-radius:99px; white-space:nowrap; }
  .mc-badge svg { width:11px; height:11px; stroke-width:2.5; }
  .mc-badge.finalized { background:#f0fdfa; color:#0d9488; }
  .mc-badge.pending   { background:#f0f2f7; color:#9094a8; }
  .mc-badge.paid      { background:#eef2ff; color:#3b6ef8; }
  .mc-badge.unpaid    { background:#fef2f2; color:#ef4444; }
  .mc-days   { display:flex; align-items:center; gap:5px; font-size:.72rem; color:#9094a8; margin-bottom:6px; }
  .mc-days svg { width:13px; height:13px; stroke-width:1.8; }
  .mc-salary { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:600; color:#0d9488; }
  .mc-salary.empty { color:#c0c5dc; font-weight:400; }
  .ty-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:900px)  { .month-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px)  { .ty-name { font-size:1.1rem; } }
  @media (max-width:600px)  { .month-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:380px)  { .month-grid { grid-template-columns:1fr; } }
`;

const initials = (name='') => name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmt = (n) => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:0,maximumFractionDigits:0})}`;

export default function OwnerAdminSalaryYear() {
  const { schoolId, teacherId } = useParams();
  const navigate = useNavigate();
  const now = new Date();
  const [year,    setYear]    = useState(now.getFullYear());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => { fetchYear(); }, [teacherId, year]);

  const fetchYear = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await api.get(`/owner/branches/${schoolId}/admin-salary/year/${year}/${teacherId}`);
      setData(res);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load salary data';
      toast.error(msg);
      setError(msg);
    }
    finally { setLoading(false); }
  };

  const isFuture = (monthStr) => {
    const [y, m] = monthStr.split('-').map(Number);
    return new Date(y, m-1, 1) > new Date(now.getFullYear(), now.getMonth(), 1);
  };

  return (
    <div className="ty-root"><style>{style}</style>
      <button className="ty-back" onClick={() => navigate('/owner/admin-salary')}>
        <ChevronLeftIcon />Back to Teachers
      </button>
      <div className="ty-header">
        {data && (
          <div className="ty-teacher">
            <div className="ty-avatar">{initials(data.teacher?.name)}</div>
            <div>
              <div className="ty-name">{data.teacher?.name}</div>
              {data.teacher?.employeeId && <div className="ty-empid">{data.teacher.employeeId}</div>}
            </div>
          </div>
        )}
        <div className="year-nav">
          <button className="year-btn" onClick={() => setYear(y => y-1)}><ChevronLeftIcon /></button>
          <span className="year-label">{year}</span>
          <button className="year-btn" onClick={() => setYear(y => y+1)}
            disabled={year >= now.getFullYear()}
            style={{ opacity: year >= now.getFullYear() ? .35 : 1, cursor: year >= now.getFullYear() ? 'not-allowed' : 'pointer' }}>
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ty-loading"><div className="spinner"/>Loading…</div>
      ) : error ? (
        <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:12,padding:'1.5rem',color:'#ef4444',fontSize:'.84rem'}}>
          ⚠ {error}
        </div>
      ) : !data ? (
        <div style={{background:'#f8f9fc',borderRadius:12,padding:'1.5rem',color:'#9094a8',fontSize:'.84rem'}}>
          No data available for this teacher.
        </div>
      ) : (
        <div className="month-grid">
          {data?.months?.map((m, i) => (
            <div key={m.month} className={`month-card${isFuture(m.month) ? ' future' : ''}`}
              style={{ animationDelay:`${i*.03}s` }}
              onClick={() => !isFuture(m.month) && navigate(`/owner/admin-salary/${schoolId}/${teacherId}/${m.month}`)}>
              <div className="mc-top">
                <div className="mc-name">{m.label}</div>
                {m.isFinalized ? (
                  <span className={`mc-badge ${m.status==='paid' ? 'paid' : 'unpaid'}`}>
                    {m.status==='paid' ? '✓ Paid' : '● Unpaid'}
                  </span>
                ) : isFuture(m.month) ? (
                  <span className="mc-badge pending">Upcoming</span>
                ) : (
                  <span className="mc-badge pending"><ClockIcon />Pending</span>
                )}
              </div>
              <div className="mc-days"><CalendarDaysIcon />{m.daysPresent} day{m.daysPresent!==1?'s':''} present</div>
              <div className={`mc-salary${!m.netSalary ? ' empty' : ''}`}>{m.netSalary ? fmt(m.netSalary) : 'Not finalized'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}